import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Box,
  Typography,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon, Info as InfoIcon } from "@mui/icons-material";
import { ApiPost } from "../../../helpers/API/ApiData";
import { toast } from "react-toastify";
import { getRoles } from "../utils/roleApi";
import { EMAIL_REGEX } from "../../../utils/validation";
const ROLE_DESCRIPTION_DE: Record<string, string> = {
  "Administrator role with full access": "Administrator-Rolle mit Vollzugriff",
  "Chatbot role for Concierge team": "Chatbot-Rolle für Concierge-Team",
};

interface AdminUserFormMUIProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface RoleOption {
  _id: string;
  name: string;
  displayText?: string;
  isActive?: boolean;
}

export const AdminUserFormMUI: React.FC<AdminUserFormMUIProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    adminName: "",
    emailId: "",
    phoneNo: "",
    roleId: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchRoles();
    }
  }, [open]);

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const response = await getRoles({ page: 1, limit: 200 });
      const normalized = response.data;
      const roleData = Array.isArray(normalized?.data) ? normalized.data : [];

      const mappedRoles = roleData
        .map((role: any) => ({
          _id: role._id,
          name: role.name,
          displayText: ROLE_DESCRIPTION_DE[role.displayText] || role.displayText,
          isActive: role.activeFlag,
        }))
        .filter((role: RoleOption) => role.isActive !== false);

      setRoles(mappedRoles);
    } catch (error: any) {
      toast.error("Fehler beim Laden der Rollen");
    } finally {
      setRolesLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.adminName || formData.adminName.trim() === "") {
      newErrors.adminName = "Bitte geben Sie den Administrator-Namen ein";
    }

    if (!formData.emailId || !EMAIL_REGEX.test(formData.emailId)) {
      newErrors.emailId = "Bitte geben Sie eine gültige E-Mail ein";
    }

    if (!formData.phoneNo || formData.phoneNo.trim() === "") {
      newErrors.phoneNo = "Bitte geben Sie die Telefonnummer ein";
    }

    if (!formData.roleId || formData.roleId.trim() === "") {
      newErrors.roleId = "Bitte wählen Sie eine Rolle aus";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = {
        adminName: formData.adminName,
        password: "temp123", // Temporary - backend will generate its own
        emailId: formData.emailId,
        phoneNo: formData.phoneNo,
        roleId: formData.roleId,
      };

      const response: any = await ApiPost("admin/sign-up", data);

      if (response?.status === 200) {
        toast.success(
          "Administrator erfolgreich hinzugefügt. Willkommens-E-Mail mit temporären Anmeldedaten wurde gesendet."
        );
        handleClose();
        onSuccess();
      } else {
        toast.error(
          response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut"
        );
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Etwas ist schief gelaufen. Bitte versuche es erneut"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      adminName: "",
      emailId: "",
      phoneNo: "",
      roleId: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Neuen Administrator erstellen</span>
          <IconButton
            aria-label="close"
            sx={{ color: "grey.500" }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "info.light",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <InfoIcon sx={{ color: "info.main" }} />
          <Typography variant="body2">
            Ein temporäres Passwort wird automatisch generiert und per E-Mail an den
            neuen Administrator gesendet. Der Administrator muss das Passwort bei der ersten Anmeldung
            ändern. Wählen Sie eine Rolle aus, um die Berechtigungen festzulegen.
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Administrator Name *"
                name="adminName"
                value={formData.adminName}
                onChange={handleChange}
                error={!!errors.adminName}
                helperText={errors.adminName}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-Mail *"
                name="emailId"
                type="email"
                value={formData.emailId}
                onChange={handleChange}
                error={!!errors.emailId}
                helperText={errors.emailId}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telefonnummer *"
                name="phoneNo"
                type="tel"
                value={formData.phoneNo}
                onChange={handleChange}
                error={!!errors.phoneNo}
                helperText={errors.phoneNo}
                variant="outlined"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Rolle *"
                name="roleId"
                value={formData.roleId}
                onChange={handleChange}
                error={!!errors.roleId}
                helperText={errors.roleId}
                variant="outlined"
                disabled={rolesLoading}
              >
                <MenuItem value="" disabled>
                  {rolesLoading ? "Rollen werden geladen..." : "Bitte auswählen"}
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role._id} value={role._id}>
                    {role.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          color="primary"
        >
          {loading ? "Wird gespeichert..." : "Administrator erstellen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
